import { PublicMetadata } from "./components/PublicMetadata";
import { useEffect, useState, type ReactNode } from "react";
import {
  BrowserRouter, Navigate, Outlet, Route, Routes, useLocation,
} from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { PublicLayout } from "./components/PublicLayout";
import { SocialMediaScheduler } from "./pages/SocialMediaScheduler";
import { Landing } from "./pages/Landing";
import { WhySchedulingImportant } from "./resources/WhySchedulingImportant";
import { Resources } from "./resources/Resources";
import { ResourceArticlePage } from "./resources/ResourceArticlePage";
import { RESOURCE_ARTICLES } from "./resources/catalog";
import { AppLanding } from "./pages/app";
import { HowItWorks } from "./pages/HowItWorks";
import { About } from "./pages/About";
import { Support } from "./pages/Support";
import { ContactPage } from "./contactus/ContactPage";
import { PartnersPage } from "./partner/PartnersPage";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { DataDeletion } from "./pages/DataDeletion";
import { Security } from "./pages/Security";
import { SignIn } from "./pages/SignIn";
import { SignInWithPassword } from "./pages/SignInWithPassword";
import { Connections } from "./pages/Connections";
import { Compose } from "./pages/Compose";
import { Posts } from "./pages/Posts";
import { Calendar } from "./pages/Calendar";
import { PostDetail } from "./pages/PostDetail";
import { Plans } from "./pages/Plans";
import { PlanSelection } from "./pages/PlanSelection";
import { SubscriptionConfirmation } from "./pages/SubscriptionConfirmation";
import { Pricing } from "./pages/Pricing";
import { Profile } from "./pages/Profile";
import { Media } from "./pages/Media";
import { Captions } from "./pages/Captions";
import { People } from "./pages/People";
import { Organisation } from "./pages/Organisation";
import { Agency } from "./pages/Agency";
import { Join } from "./pages/Join";
import { OAuthConsent } from "./pages/OAuthConsent";
import { Invitations } from "./pages/Invitations";
import { Waitlist } from "./pages/Waitlist";
// The partner pages: the argument for a human content creator, and the
// one-pager for the studio it hands off to.
import { ContentCreator } from "./partner/ContentCreator/content-creator";
import { Graphaize } from "./partner/ContentCreator/graphaize";
import { WorkspaceProvider, useWorkspace } from "./lib/WorkspaceContext";
import { UserSettingsProvider } from "./lib/userSettings";
// The signed-in chrome: header, favourites rail, collapsible sidebar, content.
// One import, the same way the four modules below are reached — and the reason
// no page in this file renders navigation of its own.
import { AppShell } from "./shell";
// The billing module, reached through exactly these two names. The permission
// that hides its nav link is the sidebar's business now, in `shell/nav.ts`.
import { Billing, BILLING_ROUTE } from "./billing";
import { Dashboard, DASHBOARD_ROUTE } from "./dashboard";
// The AI studio (ADR-0024). Same boundary as the two modules above: this file
// is the only one that imports it, and Compose does not import it at all —
// the two meet at the `content_drafts` table and nowhere else.
import { Studio, STUDIO_ROUTE } from "./studio";
import { InvitationNotice } from "./components/InvitationNotice";
import { isDismissed } from "./lib/invitations";
import { currentNext, safeNext, signInWithNext } from "./lib/redirect";
// The engagement module reaches the app through exactly these three names
// and this file is the only one that imports them (ADR-0016).
import { CoachToolbarButton, Engagement, EngagementProvider, StartRedirect, START_ROUTE } from "./engagement";

/** The `next=` on a sign-in URL, if it is one this app may send someone to. */
function safeNextParam(search: string): string | null {
  return safeNext(new URLSearchParams(search).get("next"));
}

/** Legal pages are deep-linked by section, so start each navigation at the top. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/**
 * `/start`, with one question asked first: is someone waiting for you to join
 * their workspace?
 *
 * Sign-in lands here, so this is the one place that sees every arrival however
 * they got in — the invitation link, the emailed code, a saved password. If an
 * invitation is open for this address it is shown before anything else, because
 * landing in your own empty workspace when a client has been waiting for a week
 * is the failure this whole path exists to fix. "Not now" is remembered, so it
 * asks once, not on every sign-in forever.
 *
 * Everything after that is ADR-0016's business, untouched.
 */
function StartGate() {
  const { ready, invitations, access } = useWorkspace();
  if (!ready) return <div className="wrap muted">Loading…</div>;
  if (invitations.some((i) => !isDismissed(i.id))) {
    return <Navigate to="/invitations" replace />;
  }
  // ADR-0023. Checked after the invitation, never before: an open invitation is
  // itself admission, and making an invited collaborator queue would break the
  // first minute of every engagement (ADR-0015).
  if (access.state === "waitlisted") {
    return <Navigate to="/waitlist" replace />;
  }
  return <StartRedirect />;
}

/**
 * Every route, without a router around them.
 *
 * Public pages render immediately and never wait on the Supabase session check. That
 * matters twice over: they paint instantly for a first-time visitor, and the first
 * client render matches the pre-rendered HTML exactly, so hydration is clean.
 */
export function AppRoutes() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const location = useLocation();
  // The gate's answer, loaded once per session by the provider (ADR-0023).
  const { ready: accessReady, access } = useWorkspace();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const signedIn = session !== null;

  /** Public pages must stay reachable signed out — Meta and Google review them without an account. */
  const publicPage = (node: ReactNode) => (
    <PublicLayout signedIn={signedIn}>{node}</PublicLayout>
  );

  /**
   * The gate and the chrome for every private route, as one layout.
   *
   * It is a layout route rather than a wrapper around each page for a reason
   * that is felt rather than read: the shell stays mounted across a navigation,
   * so moving from the dashboard to the composer does not tear down the
   * sidebar, re-read the connected accounts and reset the scroll — it swaps the
   * content and nothing else. Private pages still wait for the session check;
   * only they can be affected by it.
   */
  const privateLayout = () => {
    if (!ready) return <div className="wrap muted">Loading…</div>;
    if (session) {
      // Every private route, not just /start: otherwise a waitlisted account
      // reaches the app by typing /media. This is a redirect, not the
      // enforcement — a waitlisted account owns no workspace, so RLS refuses it
      // everything regardless of what the router does.
      if (!accessReady) return <div className="wrap muted">Loading…</div>;
      if (access.state === "waitlisted") return <Navigate to="/waitlist" replace />;
      return (
        <AppShell
          session={session}
          // An invitation nobody mentions is an invitation nobody accepts: the
          // link in the email works once, and only for whoever still has it
          // open. Passed in rather than imported by the shell, so the
          // engagement module keeps its single importer (ADR-0016).
          banners={<><InvitationNotice /><Engagement /></>}
          toolbarExtras={<CoachToolbarButton />}
        >
          <Outlet />
        </AppShell>
      );
    }
    // Where they were going travels with them. Without this, an invitation link
    // clicked while signed out loses its token at this line and the invitation
    // is never seen again.
    return <Navigate to={signInWithNext(currentNext(location))} replace />;
  };

  /** After signing in: back where they were headed, or /start. */
  const afterSignIn = safeNextParam(location.search) ?? START_ROUTE;

  return (
    <>
      <ScrollToTop />
      <PublicMetadata />
      <Routes>
        <Route path="/" element={publicPage(<Landing />)} />
        <Route path="/social-media-scheduler" element={publicPage(<SocialMediaScheduler />)} />
        <Route path="/resources/why-scheduling" element={publicPage(<WhySchedulingImportant />)} />
        <Route path="/resources" element={publicPage(<Resources />)} />
        {RESOURCE_ARTICLES.map(article => (
          <Route key={article.slug} path={`/resources/${article.slug}`} element={publicPage(<ResourceArticlePage article={article} />)} />
        ))}
        <Route path="/app" element={publicPage(<AppLanding />)} />
        <Route path="/how-it-works" element={publicPage(<HowItWorks />)} />
        <Route path="/partners" element={publicPage(<PartnersPage />)} />
        <Route path="/partners/content-creator" element={publicPage(<ContentCreator />)} />
        <Route path="/partners/graphaize" element={publicPage(<Graphaize />)} />
        <Route path="/pricing" element={publicPage(<Pricing />)} />
        <Route path="/security" element={publicPage(<Security />)} />
        <Route path="/about" element={publicPage(<About />)} />
        <Route path="/support" element={publicPage(<Support />)} />
        <Route path="/contact" element={publicPage(<ContactPage />)} />
        <Route path="/privacy" element={publicPage(<Privacy />)} />
        <Route path="/terms" element={publicPage(<Terms />)} />
        <Route path="/data-deletion" element={publicPage(<DataDeletion />)} />
        {/* Public because the confirm and unsubscribe links in the waitlist
            email land here, and whoever clicks them has no account (ADR-0023). */}
        <Route path="/waitlist" element={publicPage(<Waitlist />)} />

        {/* Aliases, so a mistyped or previously published legal URL never 404s. */}
        <Route path="/about-us" element={<Navigate to="/about" replace />} />
        <Route path="/company" element={<Navigate to="/about" replace />} />
        <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
        <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
        <Route path="/delete-data" element={<Navigate to="/data-deletion" replace />} />

        <Route
          path="/signin"
          element={signedIn ? <Navigate to={afterSignIn} replace /> : publicPage(<SignIn />)}
        />
        {/* Email and password, for the mailboxes a one-time link never reaches
            in time. Same session, same Supabase user — only the proof differs. */}
        <Route
          path="/sign-in-with-password"
          element={signedIn ? <Navigate to={afterSignIn} replace /> : publicPage(<SignInWithPassword />)}
        />
        <Route path="/signin-with-password" element={<Navigate to="/sign-in-with-password" replace />} />

        {/* Everything behind the sign-in, inside one shell. The gate is the
            layout's, so a route added here is protected by being here. */}
        <Route element={privateLayout()}>
          {/* Renders nothing: it works out which screen this person needs and
              replaces itself with it (ADR-0016). */}
          <Route path={START_ROUTE} element={<StartGate />} />
          <Route path={DASHBOARD_ROUTE} element={<Dashboard />} />
          <Route path="/compose" element={<Compose />} />
          <Route path={STUDIO_ROUTE} element={<Studio />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/posts" element={<Posts />} />
          <Route path="/posts/:id" element={<PostDetail />} />
          <Route path="/connections" element={<Connections />} />
          <Route path="/media" element={<Media />} />
          <Route path="/captions" element={<Captions />} />
          <Route path="/people" element={<People />} />
          <Route path="/organisation" element={<Organisation />} />
          <Route path="/agency" element={<Agency />} />
          <Route path="/join" element={<Join />} />
          {/* Consent for apps that sign in with SiminoSocial (Supabase OAuth server). */}
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          <Route path="/invitations" element={<Invitations />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/plan-selection" element={<PlanSelection />} />
          <Route path="/subscription-confirmation" element={<SubscriptionConfirmation />} />
          <Route path={BILLING_ROUTE} element={<Billing />} />
          {/* The layout has already refused anyone without a session, so the
              fallback here is unreachable — it exists because the compiler
              cannot see that. */}
          <Route path="/profile" element={session ? <Profile session={session} /> : null} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <UserSettingsProvider>
        <WorkspaceProvider>
          <EngagementProvider>
            <AppRoutes />
          </EngagementProvider>
        </WorkspaceProvider>
      </UserSettingsProvider>
    </BrowserRouter>
  );
}
